import { useState, type FormEvent } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, Users } from "lucide-react"
import { toast } from "sonner"
import { ROLE_LABELS, ROLE_SUMMARY, type MembershipSummary } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout, AuthSwitch } from "@/components/auth/auth-layout"
import { describeAuthError } from "@/lib/auth-errors"
import { Field, MiniAvatar } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { authClient } from "@/lib/auth-client"
import { invitationPreviewQuery, meQuery, rememberWorkspace } from "@/lib/queries"

export const Route = createFileRoute("/invite/$token")({ component: JoinTeam })

/** Screen 03. Shows who invited whom and the role before anything is created. */
function JoinTeam() {
  const { token } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const preview = useQuery(invitationPreviewQuery(token))
  const me = useQuery({ ...meQuery, retry: false })
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  const signedIn = me.isSuccess
  const emailMatches = signedIn && preview.data && me.data.email.toLowerCase() === preview.data.email.toLowerCase()

  async function accept() {
    const membership = await api<MembershipSummary>(`/invitations/${token}/accept`, { method: "POST" })
    await queryClient.invalidateQueries({ queryKey: ["me"] })
    rememberWorkspace(membership.workspace.slug)
    toast.success(`Welcome to ${membership.workspace.name}`)
    await navigate({ to: "/w/$workspace/inbox", params: { workspace: membership.workspace.slug } })
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!preview.data) return
    setError("")
    setBusy(true)
    try {
      if (!signedIn) {
        if (password.length < 8) throw new Error("Use at least 8 characters for your password.")
        const result = await authClient.signUp.email({ email: preview.data.email, password, name })
        if (result.error) throw result.error
      }
      await accept()
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.title : describeAuthError(err))
    } finally {
      setBusy(false)
    }
  }

  if (preview.isError) {
    return (
      <AuthLayout mark={<Users size={23} />} title="This invitation isn’t valid" subtitle="The link may have been cancelled or replaced by a newer one. Ask your teammate to send a fresh invitation.">
        <Button variant="outline" className="w-full" asChild>
          <Link to="/sign-in">Go to sign in</Link>
        </Button>
      </AuthLayout>
    )
  }
  if (!preview.data) return null
  const inv = preview.data
  const closed = inv.status !== "pending"

  return (
    <AuthLayout
      mark={<Users size={23} />}
      title="You’re invited to the team"
      subtitle={`${inv.inviterName} invited you to ${inv.workspaceName}.`}
      footer={
        !signedIn ? (
          <AuthSwitch prompt="Already have an account?">
            <Button variant="link" asChild>
              <Link to="/sign-in" search={{ redirect: `/invite/${token}` }}>
                Sign in
              </Link>
            </Button>
          </AuthSwitch>
        ) : undefined
      }
    >
      <div className="ws-invite-summary">
        <MiniAvatar name={inv.workspaceName} />
        <div>
          <strong>{inv.workspaceName}</strong>
          <p>{inv.email}</p>
        </div>
        <Badge variant="secondary">{ROLE_LABELS[inv.role]}</Badge>
      </div>
      {closed ? (
        <p className="text-sm text-muted-foreground">
          This invitation has {inv.status === "expired" ? "expired" : inv.status === "accepted" ? "already been accepted" : "been cancelled"}. Ask {inv.inviterName} for a new one.
        </p>
      ) : signedIn && !emailMatches ? (
        <div className="ws-auth-confirmation">
          <p>
            You are signed in as {me.data.email}, but this invitation was sent to {inv.email}. Sign out and continue with that address.
          </p>
          <Button className="w-full" variant="outline" onClick={() => void authClient.signOut().then(() => queryClient.resetQueries())}>
            Sign out
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="ws-auth-fields">
          {!signedIn && (
            <>
              <Field label="Your full name">{(id) => <Input id={id} value={name} required autoComplete="name" autoFocus onChange={(e) => setName(e.target.value)} />}</Field>
              <Field label="Create a password" hint="Use at least 8 characters.">
                {(id) => <Input id={id} type="password" value={password} required autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />}
              </Field>
            </>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <Button className="w-full" size="lg" type="submit" disabled={busy}>
            Accept invitation
            <ArrowRight />
          </Button>
          <p className="ws-hint">
            As {ROLE_LABELS[inv.role] === "Admin" ? "an" : "a"} {ROLE_LABELS[inv.role]}, you can {ROLE_SUMMARY[inv.role].charAt(0).toLowerCase() + ROLE_SUMMARY[inv.role].slice(1)}
          </p>
        </form>
      )}
    </AuthLayout>
  )
}
